import type { RoiNumberInput } from './engine.js';

/**
 * The texts of the two simulators, copied as they are (wrmarketing/portal,
 * public/ROI_Simulator.html and ROI_Simulator_FR.html), so the portal, Show
 * to client, the PDF and the prospect page say exactly what sellers know.
 * test/roi-engine.test.ts checks each one against the simulators' pages.
 *
 * The FR simulator keeps a few English texts (hours mode, kg hint, "Annual
 * recovered tons by sorters"): copied as they are too. The only text that
 * changes is "Year 10 ROI %", now "ROI over N years" (roiOverYears); the EN key
 * figures take the prototype's words.
 */
export interface RoiLabels {
  title: string;
  subtitle: string;
  disclaimerTitle: string;
  disclaimer: string;
  /** Section titles, 1 to 8. */
  sections: readonly [string, string, string, string, string, string, string, string];
  inputs: Record<RoiNumberInput | 'hoursMode', string>;
  hoursModes: { weekly: string; total: string };
  /** Under the extra hours field. */
  hoursInfo: { weekly: string; total: string };
  kgHint: { baseline: string; optimized: string };
  results: {
    tonsPerSorterDay: string;
    totalSorterTonsDisplay: string;
    sorterRevenue: string;
    sorterSalariesTotal: string;
    sorterMargin: string;
    disposalCostTotal: string;
    tonsPerRobotDay: string;
    totalRobotTons: string;
    robotDisposalAvoid: string;
    annualOpCost: string;
    robotProfit: string;
    robotMargin: string;
    payback: string;
    roiYear1: string;
    roiYear5: string;
  };
  /**
   * The three key figures above the simulator (prototype: .roi-mini): the
   * prototype's words in EN, the simulator's in FR.
   */
  keyFigures: { payback: string; roiYear1: string; profit: string };
  /** Replaces "Year 10 ROI %" (decision of Oct 28, 2026). */
  roiOverYears(years: string): string;
  /** Shown instead of the simulator's 999.00 when the robots make no profit. */
  noPayback: string;
  comparison: {
    metric: string;
    robots: string;
    humans: string;
    tons: string;
    revenue: string;
    profit: string;
    margin: string;
    ratio: string;
  };
  strategies: {
    addRobots: string;
    addRobotsNote: string;
    replaceSorters: string;
    replaceSortersQuestion: string;
    metric: string;
    addRobotsHead: string;
    replaceSortersHead: string;
    configuration: string;
    sorters: string;
    robots: string;
    annualSorting: string;
    sortersSorting: string;
    robotsSorting: string;
    totalSorted: string;
    vsStatusQuo: string;
    annualCosts: string;
    salaries: string;
    robotsCost: string;
    totalCost: string;
    annualRevenue: string;
    total: string;
    profitGroup: string;
    annualProfit: string;
    salarySavings: string;
  };
  extended: {
    head: string;
    sorters: string;
    extraHours: string;
    sortersExtra: string;
    robotsExtra: string;
    totalExtra: string;
    vsAddRobots: string;
    salaries: string;
    robots: string;
  };
}

const plural = (years: string, one: string, many: string) =>
  `${years} ${Number(years) === 1 ? one : many}`;

export const ROI_LABELS: Record<'en' | 'fr', RoiLabels> = {
  en: {
    title: 'Waste Robotics C&D ROI Simulator',
    subtitle: 'Optimize your C&D waste sorting',
    disclaimerTitle: 'DISCLAIMER:',
    disclaimer:
      'This simulator is a preliminary analysis tool for discussion purposes only. It is not a business proposal, quotation, or contractual document. Results are based on assumptions and may not reflect actual outcomes. Waste Robotics makes no warranty regarding accuracy and assumes no liability for decisions based on this tool.',
    sections: [
      '1. Client Parameters',
      '2. Client Results - Manual Sorters',
      '3. Robot Parameters',
      '4. Robot Results',
      '5. Robot Only - Payback & ROI',
      '6. $100,000 Investment Comparison',
      '7. Compare Strategies: Add Robots vs Replace Sorters',
      '8. Extended Hours / Reruns',
    ],
    inputs: {
      annualTonnage: 'Tonnage to be sorted (yearly)',
      disposalCost: 'Disposal cost (gate fee + tax + transport) in $/ton',
      operatingDays: 'Days of operation per year',
      hoursPerDay: 'Hours of working production per day',
      sortorsPerShift: 'Manual sorters per shift',
      pickerCost: 'Annual picker cost',
      totalSorterTonsInput: 'Recovered tons by all sorters per year',
      capexRobot: 'Capex (robot system + integration cost)',
      numRobots: 'Number of robots',
      depreciationPeriod: 'Amortization period',
      maintenancePercent: 'Opex/Maintenance (% of capex per year)',
      robotPicksPerMin: 'Number of picks per robot per minute',
      robotKgPerPick: 'Avg KG per pick (ROBOT)',
      sortersReplace: 'How many to replace?',
      s10SortersCount: 'Number of manual sorters (0 for rerun)',
      hoursMode: 'Extra hours mode & Input',
      extraHours: 'Extra hours mode & Input',
    },
    hoursModes: { weekly: 'Hours/week', total: 'Total hours/year' },
    hoursInfo: { weekly: 'per week', total: 'total hours/year' },
    kgHint: { baseline: 'Baseline', optimized: 'Optimized' },
    results: {
      tonsPerSorterDay: 'Recovered tons per sorter per day',
      totalSorterTonsDisplay: 'Annual recovered tons by sorters',
      sorterRevenue: 'Avoided cost from manual sorting',
      sorterSalariesTotal: 'Salaries total',
      sorterMargin: 'Gross margin %',
      disposalCostTotal: 'Residual cost of disposal',
      tonsPerRobotDay: 'Recovered tons per robot per day',
      totalRobotTons: 'Recovered tons by all robots per year',
      robotDisposalAvoid: 'Cost of disposal avoided (profit)',
      annualOpCost: 'Annual operating cost',
      robotProfit: 'Annual gross profit',
      robotMargin: 'Gross margin %',
      payback: 'Payback (years)',
      roiYear1: 'Year 1 ROI %',
      roiYear5: 'Year 5 ROI %',
    },
    keyFigures: { payback: 'Payback', roiYear1: 'ROI year 1', profit: 'Annual profit' },
    roiOverYears: (years) => `ROI over ${plural(years, 'year', 'years')}`,
    noPayback: 'No payback',
    comparison: {
      metric: 'Metric',
      robots: '$100k in Robots',
      humans: '$100k in Human Sorters',
      tons: 'Annual recovered tons',
      revenue: 'Annual revenue',
      profit: 'Annual gross profit',
      margin: 'Gross margin %',
      ratio: 'Profit per $1 spent',
    },
    strategies: {
      addRobots: 'Add Robots',
      addRobotsNote: 'Keep all sorters + add robots (from Section 2)',
      replaceSorters: 'Replace Sorters',
      replaceSortersQuestion: 'How many to replace?',
      metric: 'Metric',
      addRobotsHead: 'Keep all sorters + add robots',
      replaceSortersHead: 'Replace sorters',
      configuration: 'CONFIGURATION',
      sorters: 'Sorters',
      robots: 'Robots',
      annualSorting: 'ANNUAL SORTING (tons)',
      sortersSorting: 'Sorters sorting',
      robotsSorting: 'Robots sorting',
      totalSorted: 'TOTAL SORTED',
      vsStatusQuo: 'vs Status Quo',
      annualCosts: 'ANNUAL COSTS',
      salaries: 'Salaries',
      robotsCost: 'Robots (Capex + Opex)',
      totalCost: 'TOTAL COST',
      annualRevenue: 'ANNUAL REVENUE',
      total: 'Total',
      profitGroup: 'PROFIT',
      annualProfit: 'Annual profit',
      salarySavings: 'Salary Savings/year',
    },
    extended: {
      head: 'Extended Hours',
      sorters: 'Number of sorters',
      extraHours: 'Total extra hours per year',
      sortersExtra: 'Sorters extra sorting',
      robotsExtra: 'Robots extra sorting',
      totalExtra: 'TOTAL EXTRA SORTED',
      vsAddRobots: 'vs Scenario 8',
      salaries: 'Salaries (extra hours)',
      robots: 'Robots',
    },
  },
  fr: {
    title: 'Waste Robotics - Simulateur de ROI',
    subtitle: 'Optimiser le tri robotisé de DIB',
    disclaimerTitle: 'AVIS DE NON-RESPONSABILITÉ :',
    disclaimer:
      "Ce simulateur est un outil d'analyse préliminaire à des fins de discussion uniquement. Ce n'est pas une proposition d'affaires, un devis, ou un document contractuel. Les résultats sont basés sur des hypothèses et peuvent ne pas refléter les résultats réels. Waste Robotics ne donne aucune garantie quant à l'exactitude et décline toute responsabilité pour les décisions basées sur cet outil.",
    sections: [
      '1. Paramètres client',
      '2. Résultats client - Tri manuel',
      '3. Paramètre tri robotisé',
      '4. Résultats tri robotisé',
      '5. Tri robotisé seulement - Payback & ROI',
      '6. Comparaison par 100 000 € investis',
      '7. Comparaison de stratégies : Ajouter des robots vs Remplacer des opérateurs manuels',
      '8. Heures supplémentaires / sur-tri',
    ],
    inputs: {
      annualTonnage: 'Tonnage à trier (par an)',
      disposalCost: "Coût d'élimination (€, tout inclus)",
      operatingDays: "Nombre de jours d'opération par an",
      hoursPerDay: "Nombre d'heures d'opération par jour",
      sortorsPerShift: "Nombre d'opérateurs manuels par jour",
      pickerCost: 'Coût total par opérateurs manuel par an',
      totalSorterTonsInput: 'Tonnes triées manuellement par an',
      capexRobot: 'Investissement en capital € (Système robotisé et intégration)',
      numRobots: 'Nombre de robots',
      depreciationPeriod: "Nombre d'années d'amortissement",
      maintenancePercent: 'Opex/Maintenance € (% du CAPEX par an)',
      robotPicksPerMin: 'Nombre de prises par minute par robot',
      robotKgPerPick: 'Poids moyen des prises (kg)',
      sortersReplace: "Nombre d'opérateurs remplacés?",
      s10SortersCount: "Nombre d'opérateurs manuels (0 pour sur-tri)",
      hoursMode: 'Heures supplémentaires mode & Input',
      extraHours: 'Heures supplémentaires mode & Input',
    },
    hoursModes: { weekly: 'Hours/week', total: 'Total hours/year' },
    hoursInfo: { weekly: 'per week', total: 'total hours/year' },
    kgHint: { baseline: 'Baseline', optimized: 'Optimized' },
    results: {
      tonsPerSorterDay: 'Tonnes triées par jour par opérateur',
      totalSorterTonsDisplay: 'Annual recovered tons by sorters',
      sorterRevenue: "Coût d'enfouissement sauvé par le tri manuel €",
      sorterSalariesTotal: 'Coût total trieurs manuels €',
      sorterMargin: 'Marge brute %',
      disposalCostTotal: "Coût d'élimination matières résiduelles €",
      tonsPerRobotDay: 'Tonnes triées par robot par jour',
      totalRobotTons: 'Tonnes triés par système par an',
      robotDisposalAvoid: "Coût d'élimination sauvé par le tri robotisé €",
      annualOpCost: "Coût d'opération annuel €",
      robotProfit: 'Profit brut annuel',
      robotMargin: 'Marge brute %',
      payback: 'Payback (ans)',
      roiYear1: 'An 1 ROI %',
      roiYear5: 'An 5 ROI %',
    },
    keyFigures: { payback: 'Payback (ans)', roiYear1: 'An 1 ROI %', profit: 'Profit brut annuel' },
    roiOverYears: (years) => `ROI sur ${plural(years, 'an', 'ans')}`,
    noPayback: 'Pas de payback',
    comparison: {
      metric: 'Métriques',
      robots: '100 000 € en robots',
      humans: '100 000 € en opérateurs manuels',
      tons: 'Tonnes triées par an',
      revenue: 'Revenu annuel',
      profit: 'Profit brut annuel',
      margin: 'Marge brute %',
      ratio: 'Profit par 1€ dépensé',
    },
    strategies: {
      addRobots: 'Ajouter des robots',
      addRobotsNote: 'Garder tous les opérateurs + ajouter des robots (voir Section 2)',
      replaceSorters: 'Remplacer les opérateurs',
      replaceSortersQuestion: "Nombre d'opérateurs remplacés?",
      metric: 'Métriques',
      addRobotsHead: 'Garder tous les opérateurs + ajouter des robots',
      replaceSortersHead: 'Remplacer les opérateurs',
      configuration: 'CONFIGURATION',
      sorters: 'Opérateurs manuels',
      robots: 'Robots',
      annualSorting: 'Tonnage trié annuel',
      sortersSorting: 'Tri manuel',
      robotsSorting: 'Tri robotisé',
      totalSorted: 'Tri total',
      vsStatusQuo: 'vs Status Quo',
      annualCosts: 'Coût annuel',
      salaries: "Coût main d'oeuvre",
      robotsCost: 'Robots (Capex + Opex)',
      totalCost: 'Coût total',
      annualRevenue: 'Revenu annuel',
      total: 'Total',
      profitGroup: 'PROFIT',
      annualProfit: 'Profit annuel',
      salarySavings: "Épargne cout de main d'oeuvre par an",
    },
    extended: {
      head: 'Heures supplémentaires',
      sorters: "Nombre d'opérateurs",
      extraHours: 'Total heures additionnelles par an',
      sortersExtra: 'Tonnes triées additionnelles manuellement',
      robotsExtra: 'Tonnes triés additionnelles robots',
      totalExtra: 'Tonnage additionnel total',
      vsAddRobots: 'vs Ajouter des robots',
      salaries: "Cout main d'oeuvre additionnel",
      robots: 'Robots',
    },
  },
};
